import type { ClaimRecord, SortingComparatorName } from "../types";
import { type DerivableField, deriveDisplayValue } from "./valueDerivations";

/**
 * Orders two claims for one column. Negative when the first sorts earlier,
 * positive when it sorts later, and zero when the two are equivalent.
 */
type ClaimComparator = (a: ClaimRecord, b: ClaimRecord) => number;

/**
 * Binds a comparator to the column or export field it orders, so the result
 * drops straight into a Cloudscape column's `sortingComparator`.
 */
type SortingComparatorFactory = (definition: DerivableField) => ClaimComparator;

/**
 * The implemented sorting comparators, keyed by the name a config declares.
 *
 * This module deliberately imports only `valueDerivations`, and no renderer or
 * column builder. The config validator imports it to check that a declared
 * comparator name exists, so anything reachable from here becomes part of the
 * validator's dependency graph.
 */
export const SORTING_COMPARATORS: Record<SortingComparatorName, SortingComparatorFactory> = {
  /**
   * Orders by what the cell shows rather than by the underlying property, so a
   * derived column does not sort by a value the user cannot see. Identical
   * derived values compare as zero, which is what lets Cloudscape's stable sort
   * hold their existing relative order.
   */
  derivedDisplayValue: (definition) => (a, b) =>
    deriveDisplayValue(definition, a).localeCompare(deriveDisplayValue(definition, b)),
};
